import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Card,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import CategoryService from "../../../Services/QRCodeServices/MenuCategory/CategoryService";
import CurrencyService from "../../../Services/QRCodeServices/CurrencyService/CurrencyService";
import UnitService from "../../../Services/QRCodeServices/UnitService/UnitService";
import axiosInstance from "../../../interceptors/axiosInstance";
import { ORG_MENUITEMS_CREATE } from "../../../api/endpoints";
import toast from "react-hot-toast";

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const AddMenuItem = ({ isOpen, toggleModal, onSubmit, fetchCategoryData }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [data, setData] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [submitClicked, setSubmitClicked] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));

  const initialValues = {
    title: "",
    description: "",
    ingredients: "",
    unitId: "",
    image: null,
    menuCategoryId: "",
    price: "0",
    recipePrice: "0",
    currency: "",
    MenuAttributes: [],
    isPurchase: false,
    hasStock: false,
    isSpecial: false,
    IncludeInSales: true,
  };

  const handleRemoveAttribute = (index, values, setFieldValue) => {
    const newAttributes = values.MenuAttributes.filter((_, i) => i !== index);
    setFieldValue("MenuAttributes", newAttributes);
    if (newAttributes.length === 0) {
      setFieldValue("price", "");
    }
  };

  const handleAddAttribute = (values, setFieldValue) => {
    setFieldValue("MenuAttributes", [
      ...values.MenuAttributes,
      { name: "", rate: 0 },
    ]);
    if (values.MenuAttributes.length === 0) {
      setFieldValue("price", "0");
    }
  };

  const getValidationSchema = (values) => {
    const baseSchema = {
      title: Yup.string().required("Title is required"),
      description: Yup.string(),
      ingredients: Yup.string(),
      unitId: Yup.string().required("Unit is required"),
      image: Yup.mixed().nullable(),
      menuCategoryId: Yup.string().required("Menu Category is required"),
      currency: Yup.string().required("Currency is required"),
      MenuAttributes: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required("Item name is required"),
          rate: Yup.number().required("Item rate is required"),
        })
      ),
    };

    if (values.MenuAttributes.length === 0) {
      baseSchema.price = Yup.number().required("Price is required");
    }

    if (values.MenuAttributes.length < 0) {
      baseSchema.price = Yup.number().required("Price can't be negative");
    }

    return Yup.object().shape(baseSchema);
  };

  const fetchAllUnits = async () => {
    let allUnits = [];
    let pageNumber = 1;
    const pageSize = 50;
    let hasMoreData = true;

    while (hasMoreData) {
      const response = await UnitService.getList({ pageNumber, pageSize });
      allUnits = [...allUnits, ...response.data];
      hasMoreData = response.data.length === pageSize;
      pageNumber++;
    }
    return allUnits;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await CategoryService.getList();
        setData(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    const fetchCurrency = async () => {
      try {
        const response = await CurrencyService.getList();
        const options = response.data.map((item) => ({
          value: item.id,
          label: item.title,
        }));
        setCurrencyOptions(response.data);
      } catch (error) {
        console.error("Error fetching currency:", error);
      }
    };
    const fetchUnit = async () => {
      try {
        const allUnits = await fetchAllUnits();
        const options = allUnits.map((item) => ({
          value: item.id,
          label: item.title,
        }));
        setUnitOptions(options);
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchUnit();
    fetchCurrency();
    fetchData();
  }, []);

  const handleAcceptedFiles = (files, setFieldValue) => {
    const formattedFiles = files.map((file) => ({
      ...file,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
    }));

    setSelectedFiles([...formattedFiles]);

    if (files.length > 0) {
      setFieldValue("image", files[0]);
    }
  };

  const handleFileRemove = (indexToRemove, setFieldValue) => {
    setSelectedFiles((prevFiles) => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (newFiles.length === 0) {
        setFieldValue("image", null);
      }
      return newFiles;
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("ingredients", values.ingredients);
      formData.append("unitId", values.unitId);
      formData.append("menuCategoryId", values.menuCategoryId);
      formData.append(
        "price",
        values.MenuAttributes.length > 0 ? "0" : values.price
      );
      formData.append("recipePrice", values.recipePrice);
      formData.append("currencyId", values.currency);
      formData.append("isPurchase", values.isPurchase);
      formData.append("hasStock", values.hasStock);
      formData.append("isSpecial", values.isSpecial);
      formData.append("IncludeInSales", values.IncludeInSales);

      if (values.image) {
        formData.append("image", values.image);
      }

      // if (values.MenuAttributes.length > 0) {
      //   formData.append(
      //     "MenuAttributesDto.MenuAttributes",
      //     JSON.stringify(values.MenuAttributes)
      //   );
      // }
      values.MenuAttributes.forEach((attribute, index) => {
        formData.append(
          `MenuAttributesDto.MenuAttributes[${index}].name`,
          attribute.name
        );
        formData.append(
          `MenuAttributesDto.MenuAttributes[${index}].rate`,
          attribute.rate
        );
      });

      const response = await axiosInstance.post(
        ORG_MENUITEMS_CREATE,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      );

      // fetchCategoryData();
      // toggleModal();
      setTimeout(() => {
        toast.success("Item is submmitted successful");
        fetchCategoryData();
        setLoading(false);
        toggleModal(); // Close modal after submission
      }, 2500);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit item. Please try again.");
      setLoading(false); // Reset loading state on error
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg">
      <ModalHeader toggle={toggleModal}>Add Menu Item</ModalHeader>
      <ModalBody>
        <Formik
          initialValues={initialValues}
          validate={(values) => {
            const schema = getValidationSchema(values);
            try {
              schema.validateSync(values, { abortEarly: false });
              return {};
            } catch (err) {
              return err.inner.reduce((acc, error) => {
                acc[error.path] = error.message;

                return acc;
              }, {});
            }
          }}
          // onSubmit={handleSubmit}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          {({ values, setFieldValue, errors, isSubmitting }) => {
            useEffect(() => {
              if (Object.keys(errors).length > 0) {
                Object.values(errors).forEach((error) => toast.error(error));
              }
            }, [submitClicked]);

            return (
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Dropzone
                        onDrop={(acceptedFiles) =>
                          handleAcceptedFiles(acceptedFiles, setFieldValue)
                        }
                      >
                        {({ getRootProps, getInputProps }) => (
                          <div className="dropzone dz-clickable pt-3">
                            <div
                              className="dz-message needsclick mb-5 p-3"
                              {...getRootProps()}
                            >
                              <i className="align-item-center display-4 text-muted ri-upload-cloud-2-fill" />
                              <input {...getInputProps()} />
                              <h6 className="fs-15 fw-semi-bold pt-3">
                                Drop files here or{" "}
                                <Link to="#">click to upload.</Link>
                              </h6>
                              {selectedFiles.map((f, i) => (
                                <Card
                                  key={i}
                                  className="mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                >
                                  <Row className="align-items-center">
                                    <Col className="col-auto">
                                      <img
                                        height="100"
                                        className="avatar-sm rounded bg-light"
                                        alt={f.name}
                                        src={f.preview}
                                      />
                                    </Col>
                                    <Col>
                                      <Link
                                        to="#"
                                        className="text-muted font-weight-bold"
                                      >
                                        {f.name}
                                      </Link>
                                      <p className="mb-0 fs-12">
                                        {f.formattedSize}
                                      </p>
                                    </Col>
                                    <Col lg={2}>
                                      <Link
                                        onClick={() =>
                                          handleFileRemove(i, setFieldValue)
                                        }
                                      >
                                        <h6>
                                          <i className="ri-delete-bin-2-line text-danger"></i>
                                        </h6>
                                      </Link>
                                    </Col>
                                  </Row>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}
                      </Dropzone>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="title">
                            Title <span className="text-danger">*</span>
                          </Label>
                          <Field
                            name="title"
                            type="text"
                            className="form-control"
                            id="title"
                          />
                          <ErrorMessage
                            name="title"
                            component="div"
                            className="text-danger"
                          />
                          {/* {isSubmitting && errors.title && toast.error(errors.title)} */}
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="description">Description </Label>
                          <Field
                            name="description"
                            type="text"
                            className="form-control"
                            id="description"
                          />
                          <ErrorMessage
                            name="description"
                            component="div"
                            className="text-danger"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label>Menu Attributes</Label>
                          {values.MenuAttributes.map((attr, index) => (
                            <Row
                              key={index}
                              className="align-items-center mb-3"
                            >
                              <Col md={5}>
                                <Field
                                  name={`MenuAttributes[${index}].name`}
                                  type="text"
                                  className="form-control"
                                  placeholder="Item Name"
                                />
                                <ErrorMessage
                                  name={`MenuAttributes[${index}].name`}
                                  component="div"
                                  className="text-danger"
                                />
                              </Col>
                              <Col md={5}>
                                <Field
                                  name={`MenuAttributes[${index}].rate`}
                                  type="number"
                                  className="form-control"
                                  placeholder="Attribute Rate"
                                />
                                <ErrorMessage
                                  name={`MenuAttributes[${index}].rate`}
                                  component="div"
                                  className="text-danger"
                                />
                              </Col>
                              <Col md={2} className="text-end">
                                <Button
                                  color="danger"
                                  className="btn-sm"
                                  onClick={() =>
                                    handleRemoveAttribute(
                                      index,
                                      values,
                                      setFieldValue
                                    )
                                  }
                                >
                                  <i className="ri-delete-bin-2-line"></i>
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Button
                            color="primary"
                            className="btn-sm ms-2"
                            onClick={() =>
                              handleAddAttribute(values, setFieldValue)
                            }
                          >
                            +
                          </Button>
                        </FormGroup>
                      </Col>

                      {values.MenuAttributes.length === 0 && (
                        <>
                          {" "}
                          <Col md={6}>
                            <FormGroup>
                              <Label for="price">
                                Price <span className="text-danger">*</span>
                              </Label>
                              <Field
                                name="price"
                                type="number"
                                className="form-control"
                                id="price"
                              />
                              <ErrorMessage
                                name="price"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="recipePrice">
                                Recipe Price{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Field
                                name="recipePrice"
                                type="number"
                                className="form-control"
                                id="recipePrice"
                              />
                              <ErrorMessage
                                name="recipePrice"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                        </>
                      )}
                      <>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="currency">
                              Currency <span className="text-danger">*</span>
                            </Label>
                            <Field
                              name="currency"
                              as="select"
                              className="form-control"
                              id="currency"
                            >
                              <option value="" disabled>
                                Select Currency
                              </option>
                              {currencyOptions?.map((item, index) => (
                                <option key={index} value={item.id}>
                                  {item.title}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="currency"
                              component="div"
                              className="text-danger"
                            />
                            {/* {isSubmitting && errors.currency && toast.error(errors.currency)} */}
                          </FormGroup>
                        </Col>

                        <Col md={6}>
                          <FormGroup>
                            <Label for="unitId">
                              Unit <span className="text-danger">*</span>
                            </Label>
                            <Field
                              name="unitId"
                              as="select"
                              className="form-control"
                              id="unitId"
                            >
                              <option value="" disabled>
                                Select Unit
                              </option>
                              {unitOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="unitId"
                              component="div"
                              className="text-danger"
                            />
                          </FormGroup>
                          {/* {isSubmitting && errors.unitId && toast.error(errors.unitId)} */}
                        </Col>
                      </>

                      <Col md={12}>
                        <FormGroup>
                          <Label for="menuCategoryId">
                            Menu Category <span className="text-danger">*</span>
                          </Label>
                          <Field
                            name="menuCategoryId"
                            as="select"
                            className="form-control"
                            id="menuCategoryId"
                          >
                            <option value="" disabled>
                              Select Category
                            </option>
                            {data?.map((item, index) => (
                              <option key={index} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="menuCategoryId"
                            component="div"
                            className="text-danger"
                          />
                          {/* {isSubmitting && errors.menuCategoryId && toast.error(errors.menuCategoryId)} */}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <div className="form-check">
                            <Field
                              type="checkbox"
                              name="isPurchase"
                              className="form-check-input"
                              id="isPurchase"
                            />
                            <Label
                              className="form-check-label"
                              for="isPurchase"
                            >
                              Purchase
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup>
                          <div className="form-check">
                            <Field
                              type="checkbox"
                              name="hasStock"
                              className="form-check-input"
                              id="hasStock"
                            />
                            <Label className="form-check-label" for="hasStock">
                              Stock
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <div className="form-check">
                            <Field
                              type="checkbox"
                              name="isSpecial"
                              className="form-check-input"
                              id="isSpecial"
                            />
                            <Label className="form-check-label" for="isSpecial">
                              Special
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <div className="form-check">
                            <Field
                              type="checkbox"
                              name="IncludeInSales"
                              className="form-check-input"
                              id="IncludeInSales"
                            />
                            <Label
                              className="form-check-label"
                              for="IncludeInSales"
                            >
                              Include In Sales
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <ModalFooter>
                  <Button color="danger" onClick={toggleModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    onClick={() => setSubmitClicked((prev) => !prev)}
                    color={loading ? "secondary" : "success"}
                    disabled={loading}
                  >
                    {loading && (
                      <Spinner size="sm" className="me-2">
                        Loading...
                      </Spinner>
                    )}{" "}
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default AddMenuItem;
